'use client';

import Link from 'next/link';
import { useState } from 'react';
import { usePathname } from 'next/navigation';
import LogoImage from '@/public/updated.png';
import DarkLogo from '@/public/dark-logo.png';
import Image from 'next/image';
import DropDown from '@/components/layout/navbar/new/DropDown';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useTheme } from '@/context/ThemeContext';

export const Navbar = ({ data }) => {
  const pathname = usePathname();
  const isSingleBlogPage = pathname.startsWith('/blog/');

  if (isSingleBlogPage) {
    return <BlogNavbar data={data} />;
  }

  return <NavbarContent data={data} isBlogPage={false} isBlogLightTheme={false} />;
};

const BlogNavbar = ({ data }) => {
  const { theme } = useTheme();
  const isBlogLightTheme = theme === 'light';

  return (
    <NavbarContent
      data={data}
      isBlogPage
      isBlogLightTheme={isBlogLightTheme}
    />
  );
};

const NavbarContent = ({ data, isBlogPage, isBlogLightTheme }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const isMobile = useIsMobile();

  const toggleDropDown = () => {
    setShowDropdown((prev) => !prev);
  };

  function transformData(apiData) {
    const transformed = [];

    (apiData ?? []).forEach((item) => {
      if (item.label === 'Services' && item.links) {
        // Extract links from Services
        item.links.forEach((link) => {
          transformed.push({
            heading: link.link_heading,
            url: link.link_url,
            description: link.link_description,
          });
        });
      }
    });

    return transformed;
  }

  const transformedData = transformData(data?.nav_items);

  return (
    <div
      className={`top-0 right-0 left-0 z-[999] fixed bg-transparent h-0 ${
        isBlogPage ? 'absolute' : 'fixed'
      }`}
    >
      <div
        className={`
                top-0 left-0 absolute backdrop-blur-sm
                ${isBlogPage ? 'relative' : ''} 
                ${isBlogLightTheme ? ' text-black' : ' text-white'} 
                w-full
                `}
      >
        {/* main navbar */}
        <div className="md:relative flex justify-between items-center px-6 lg:px-10 h-20">
          {/* Logo */}
          <Logo isBlogLightTheme={isBlogLightTheme} />

          {/* Desktop Menu */}
          <nav className="hidden md:flex items-center md:gap-2 lg:gap-4">
            {data?.nav_items?.map((item) => {
              if (!item?.links) {
                return (
                  <Link
                    key={item.label}
                    href={item.url}
                    className="md:px-2 lg:px-5 text-nowrap leading-[20px]"
                  >
                    <span>{item.label}</span>
                    {item.icon && (
                      <span className="flex justify-center items-center w-5 h-5 size-5">
                        {item.icon}
                      </span>
                    )}
                  </Link>
                );
              } else {
                return (
                  <div
                    key={item.label}
                    className="flex items-center gap-1.5 md:px-2 lg:px-5 text-nowrap leading-[20px] cursor-pointer"
                    onClick={toggleDropDown}
                  >
                    <span>{item.label}</span>
                    <span
                      className={`flex justify-center transition-transform duration-300 items-center w-5 h-5 size-5 ${
                        showDropdown ? '-rotate-180' : 'rotate-0'
                      }`}
                    >
                      <Arrow />
                    </span>
                  </div>
                );
              }
            })}
          </nav>

          {/* Mobile toggle button */}
          <button
            className={`md:hidden flex flex-col justify-center items-center text-white transition-transform duration-200 ${
              menuOpen ? 'rotate-90 scale-110' : 'rotate-0 scale-100'
            }`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span
              className={`transition-opacity duration-200 ${
                menuOpen ? 'hidden opacity-0' : 'block opacity-100'
              }`}
            >
              <MenuIcon color={isBlogLightTheme ? 'black' : 'white'} />
            </span>
            <span
              className={`transition-opacity duration-200 ${
                menuOpen ? 'block opacity-100' : 'hidden opacity-0'
              }`}
            >
              <CloseIcon color={isBlogLightTheme ? 'black' : 'white'} />
            </span>
          </button>
        </div>
      </div>
      {/* Desktop Dropdown */}
      {!isMobile && (
        <DropDown
          visible={showDropdown}
          setVisible={setShowDropdown}
          navlist={transformedData}
        />
      )}

      <div
        className={`md:hidden top-20 left-0 z-[999] ${
          isBlogPage ? 'absolute' : 'fixed'
        }
            ${isBlogLightTheme ? ' text-black' : ' text-white'} 
              w-full backdrop-blur-sm overflow-hidden transition-all duration-300 ease-in-out ${
                menuOpen
                  ? 'max-h-[calc(100vh-80px)] opacity-100 overflow-y-auto'
                  : 'max-h-0 opacity-0'
              }`}
      >
            <nav className="flex flex-col items-start gap-11 px-[67px] pt-[50px] pb-[59px]">
              {data?.nav_items?.map((item) => {
                if (!item.links) {
                  return (
                    <Link
                      key={item.label}
                      href={item.url}
                      onClick={() => setMenuOpen(false)}
                      className="leading-[20px]"
                    >
                      <span>{item.label}</span>
                    </Link>
                  );
                } else {
                  return (
                    <div key={item.label} className="w-full">
                      <div
                        className="flex items-center gap-1.5 leading-[20px] cursor-pointer"
                        onClick={toggleDropDown}
                      >
                        <span>{item.label}</span>
                        <span
                          className={`flex justify-center items-center w-5 h-5 transition-transform duration-300 ${
                            showDropdown ? '-rotate-180' : 'rotate-0'
                          }`}
                        >
                          <Arrow />
                        </span>
                      </div>

                      <div
                        className={`flex flex-col gap-3 mt-3 overflow-hidden transition-all duration-300 ease-in-out ${
                          showDropdown
                            ? 'max-h-screen opacity-100'
                            : 'max-h-0 opacity-0'
                        }`}
                      >
                        <DropDown
                          visible={showDropdown}
                          setVisible={setShowDropdown}
                          navlist={transformedData}
                        />
                      </div>
                    </div>
                  );
                }
              })}
            </nav>
      </div>
    </div>
  );
};

const Logo = ({ isBlogLightTheme }) => {
  return (
    <Link
      href={'/'}
      className="flex justify-center items-center w-[130px] md:w-[200px] lg:min-w-[210px] min-h-[40px] Logo"
    >
      <Image
        src={isBlogLightTheme ? DarkLogo : LogoImage}
        alt="company-logo"
        width={200}
        height={37}
        className="w-full h-auto max-w-[200px]"
        priority
      />
    </Link>
  );
};

const MenuIcon = ({ color }) => {
  return (
    <svg
      width="23"
      height="13"
      viewBox="0 0 23 13"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1.8335 6.50033H21.1668M1.8335 1.66699H21.1668M1.8335 11.3337H11.5002"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const CloseIcon = ({ color }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-6 h-6"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const Arrow = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m19.5 8.25-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
};
